import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { IconCopy, IconWorldOff } from "@tabler/icons-react"
import { type FC } from "react"
import { useNavigate } from "react-router"

import routes from "@forento/core/routes"
import { toNumberFormat } from "@forento/core/utilities/number"

import {
	CommentIcon,
	DeleteIcon,
	EditIcon,
	ExamIcon,
	InputIcon,
	OpenExternalIcon,
	PreviewIcon,
	SettingsIcon,
	UsersIcon,
} from "~/components/Icon"
import ProductListRow from "~/components/ProductListRow"
import { useAlert } from "~/contexts/AlertContext"
import { useUser } from "~/contexts/UserContext"
import { query } from "~/utilities/trpc"

type Props = {
	course: {
		id: number
		title: string
		publishDate: Date
		sales: number
		enrolledStudents: number
		totalCommentsUnderReview: number
		pauseDate: Date | null
	}
	reload(): Promise<unknown>
}
const PublishedCourse: FC<Props> = ({ course, reload }) => {
	const user = useUser()
	const alert = useAlert()
	const navigate = useNavigate()

	const createLoginToken = query.user.createLoginToken.useMutation({
		onSuccess: ({ url }) => window.open(url, "_blank"),
		onError: error => console.error(error),
	})
	const duplicate = query.course.duplicate.useMutation({
		async onSuccess() {
			await reload()
			navigate("#draft")
		},
		onError: error => console.error(error),
	})
	const unpublish = query.course.unpublish.useMutation({ onSuccess: reload, onError: error => console.error(error) })
	const remove = query.course.deleteById.useMutation({ onSuccess: reload, onError: error => console.error(error) })

	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: course.id })

	async function handleDuplicate() {
		const result = await alert.confirm(
			"Duplicate course",
			`Are you sure you want to duplicate the course **${course.title}**?`,
			{ isDestructive: false },
		)
		if (!result.result) return
		await duplicate.mutateAsync(course.id)
		result.close()
	}

	async function handleUnpublish() {
		const result = await alert.confirm(
			"Unpublish course",
			`Are you sure you want to unpublish the course **${course.title}**?`,
			{ isDestructive: false },
		)
		if (!result.result) return
		await unpublish.mutateAsync(course.id)
		result.close()
	}

	async function handleDelete() {
		const warnings = [
			hasSales && `it has ${toNumberFormat(course.sales)} ${course.sales === 1 ? "sale" : "sales"}`,
			course.enrolledStudents > 0 &&
				`${toNumberFormat(course.enrolledStudents)} ${course.enrolledStudents === 1 ? "student is" : "students are"} enrolled`,
		].filter(x => x !== false)
		const result = await alert.confirm(
			"Delete course",
			`Are you sure you want to delete the course **${course.title}**? This action is irreversible.` +
				(warnings.length > 0
					? `\n\n**Warning:** ${warnings.join(" and ")}. Deleting the course removes their access to it.`
					: ""),
		)
		if (!result.result) return
		await remove.mutateAsync(course.id)
		result.close()
	}

	const hasSales = course.sales > 0

	return (
		<ProductListRow
			title={course.title}
			href={routes.app.course.detail(course.id)}
			subtitle={
				course.pauseDate
					? `Unlisted on ${course.pauseDate.toLocaleDateString()}`
					: `Published ${course.publishDate.toLocaleDateString()}`
			}
			actions={[
				...(course.totalCommentsUnderReview > 0
					? [
							{
								id: "review-comments",
								tooltip: "Review comments",
								icon: CommentIcon,
								href: routes.app.course.reviewComments(course.id),
								notification: true,
							},
						]
					: []),
				{
					id: "enrolled-students",
					tooltip: "Enrolled students",
					icon: UsersIcon,
					number: course.enrolledStudents,
					href: routes.app.course.enrolledStudents(course.id),
				},
				{ id: "edit", tooltip: "Edit", icon: EditIcon, href: routes.app.course.detail(course.id) },
				{ id: "exam", tooltip: "Exam", icon: ExamIcon, href: routes.app.course.exam(course.id) },
				{ id: "inputs", tooltip: "Inputs", icon: InputIcon, href: routes.app.course.inputs(course.id) },
				{
					id: "settings",
					tooltip: "Settings",
					icon: SettingsIcon,
					href: routes.app.course.settings(course.id),
				},
				{
					id: "preview",
					tooltip: "Preview",
					icon: PreviewIcon,
					onClick: () => createLoginToken.mutate({ platformPath: routes.platform.course.player(course.id) }),
				},
				{ id: "duplicate", tooltip: "Duplicate", icon: IconCopy, onClick: handleDuplicate },
				{
					id: "unpublish",
					tooltip: hasSales ? "You can't unpublish a course that has sales" : "Unpublish",
					icon: IconWorldOff,
					disabled: hasSales,
					onClick: handleUnpublish,
				},
				{ id: "delete", tooltip: "Delete", icon: DeleteIcon, danger: true, onClick: handleDelete },
				{
					id: "open-externally",
					tooltip: "Open externally",
					icon: OpenExternalIcon,
					href: `//${user.user!.platform!.domain.platform}${routes.platform.course.detail(course.id)}`,
				},
			]}
			dragHandle={{ attributes, listeners }}
			ref={setNodeRef}
			style={{ transform: CSS.Transform.toString(transform), transition }}
		/>
	)
}

export default PublishedCourse
